import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X, Trophy, RotateCcw } from 'lucide-react';
import { Word } from '@/data/words';

interface SpellingGameProps {
  words: Word[];
  onBack: () => void;
}

export function SpellingGame({ words, onBack }: SpellingGameProps) {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [score, setScore] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  const currentWord = words[currentWordIndex];

  const checkSpelling = () => {
    const isCorrect = userInput.toLowerCase().trim() === currentWord.word.toLowerCase();
    
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    
    if (isCorrect) {
      setScore(prev => prev + 15);
      setCorrectAnswers(prev => prev + 1);
    }

    setTimeout(() => {
      if (currentWordIndex < words.length - 1) {
        setCurrentWordIndex(prev => prev + 1);
        setUserInput('');
        setFeedback(null);
      } else {
        setGameComplete(true);
      }
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && userInput.trim()) {
      checkSpelling();
    }
  };

  const resetGame = () => {
    setCurrentWordIndex(0);
    setUserInput('');
    setFeedback(null);
    setScore(0);
    setGameComplete(false);
    setCorrectAnswers(0);
  };

  if (gameComplete) {
    return (
      <div className="flex flex-col items-center space-y-6 p-8">
        <div className="text-center">
          <Trophy className="w-20 h-20 text-yellow-500 mx-auto mb-4 animate-bounce" />
          <h2 className="text-4xl font-bold rainbow-text mb-2">Great Job!</h2>
          <p className="text-xl text-gray-600 mb-4">
            You got {correctAnswers} out of {words.length} words correct!
          </p>
          <p className="text-2xl font-bold text-primary">Final Score: {score} points</p>
        </div>
        
        <div className="flex space-x-4">
          <Button onClick={resetGame} className="btn-fun" size="lg">
            <RotateCcw className="w-5 h-5 mr-2" />
            Play Again
          </Button>
          <Button onClick={onBack} variant="outline" size="lg">
            Back to Categories
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button onClick={onBack} variant="outline">
          ← Back
        </Button>
        <div className="text-center">
          <h2 className="text-2xl font-bold rainbow-text">Spelling Game</h2>
          <p className="text-lg text-primary font-semibold">Score: {score}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-600">
            {currentWordIndex + 1} / {words.length}
          </p>
        </div>
      </div>

      {/* Game card */}
      <Card className="word-card mb-6">
        <CardContent className="p-8 text-center">
          <img
            src={currentWord.image}
            alt="Spell this word"
            className="w-48 h-48 object-cover rounded-lg mx-auto mb-6"
          />
          
          <p className="text-lg text-gray-600 mb-4">
            How do you spell this word?
          </p>
          
          <div className="space-y-4">
            <Input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer here..."
              className="text-center text-2xl font-bold py-4"
              disabled={feedback !== null}
            />
            
            {feedback && (
              <div className={`flex items-center justify-center space-x-2 text-xl font-bold ${
                feedback === 'correct' ? 'text-green-600' : 'text-red-600'
              }`}>
                {feedback === 'correct' ? (
                  <>
                    <Check className="w-6 h-6" />
                    <span>Correct! It's "{currentWord.word}"</span>
                  </>
                ) : (
                  <>
                    <X className="w-6 h-6" />
                    <span>Try again! It's "{currentWord.word}"</span>
                  </>
                )}
              </div>
            )}
            
            {!feedback && (
              <Button
                onClick={checkSpelling}
                disabled={!userInput.trim()}
                className="btn-fun"
                size="lg"
              >
                Check Spelling
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <div className="text-center p-4 bg-blue-50 rounded-lg">
        <p className="text-gray-600">
          Look at the picture and type the correct spelling of the word!
        </p>
      </div>
    </div>
  );
}